import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useState } from 'react';
import {
  Container,
  Typography,
  Divider,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
} from '@mui/material';
import { Drawer, IconButton } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Copyright from './Copyright';
import { Helmet } from 'react-helmet';
import { useIsMobile } from "./karyakramaTheme";
import { PRODUCT_NAME } from '../common/AppConstants';

const drawerWidth = 240;

{
  /* Update in App.js */
}
const sidebarItems = [
  { label: 'Overview', route: '/docs/overview' },
  { label: 'Get Started', route: '/docs/getting-started' },
  { label: 'Configure Classroom Types', route: '/docs/classroom-types' },
  { label: 'Add Classrooms', route: '/docs/classrooms' },
  { label: 'Add Subjects', route: '/docs/subjects' },
  { label: 'Add Teachers', route: '/docs/teachers' },
  { label: 'Add Student Groups', route: '/docs/students' },
  { label: 'Verify Configuration', route: '/docs/configuration' },
  { label: 'Create Time Table', route: '/docs/create-timetable' },
  { label: 'View Time Table', route: '/docs/view-timetable' },
];

function DocsLayout() {
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const location = useLocation();
  const currentPageIndex = sidebarItems.findIndex(
    (item) => item.route === location.pathname,
  );
  const prevPage =
    currentPageIndex > 0 ? sidebarItems[currentPageIndex - 1] : null;
  const nextPage =
    currentPageIndex < sidebarItems.length - 1
      ? sidebarItems[currentPageIndex + 1]
      : null;

  function DocsNavListItems({onClick}) {
    return (
      <>
        {sidebarItems.map((item) => {
          const isActive = location.pathname === item.route; // Check if the item is selected
          return (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                component={NavLink}
                to={item.route}
                sx={{
                  borderRadius: 1,
                  '&.active': {
                    color: 'primary.main',
                    fontWeight: 'bold',
                  },
                }}
                onClick={onClick}
              >
                <Typography variant={isActive ? 'h5' : 'body1'}>
                  {item.label}
                </Typography>
              </ListItemButton>
            </ListItem>
          );
        })}
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{`${PRODUCT_NAME} | Docs`}</title>
        <meta
          name="description"
          content={`Learn about how to create academic class schedules aka time table with ${PRODUCT_NAME} in this documentation.`}
        />
      </Helmet>
      <Container component="main" maxWidth="xl" sx={{ pt: 2 }}>
        {/* Title Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box
            component={NavLink}
            to="/docs"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'inherit', // Ensures it looks like normal text
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.main', // Optional hover effect
              },
            }}
          >
            <HomeIcon color="primary" fontSize="small" />
            <Typography variant="h3" sx={{ flexGrow: 1 }}>
              Documentation
            </Typography>
          </Box>

          {/* Docs Menu Button (Visible on Mobile and Tab Only) */}
          { isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="docs-menu"
              onClick={handleDrawerToggle}
              fontSize="small"
              sx={{ py: 0 }}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{ transform: 'rotate(0deg)', transition: '0.3s' }}
              />
            </IconButton>
          )}

          {/* Right-aligned clickable text */}
          <Typography
            component={NavLink}
            to="/support"
            variant="h5"
            sx={{
              ml: 'auto', // Pushes it to the right
              textDecoration: 'underline', // Always underlined
              color: 'inherit',
              '&:hover': { color: 'primary.main' }, // Changes color on hover
              cursor: 'pointer',
            }}
          >
            Support Center
          </Typography>
        </Box>
        {/* Full-width Divider */}
        {/* <Divider sx={{ width: "100vw", position: "absolute", left: 0 }} /> */}
        <Divider />

        {/* Layout */}
        <Box sx={{ display: 'flex' }}>
          {/* Sidebar */}
          { !isMobile ? (  // Desktop Version, Hidden on Mobile and Tab
            <Box
              sx={{
                width: drawerWidth,
                flexShrink: 0,
              }}
            >
              <List>
                <DocsNavListItems/>
              </List>
            </Box>
          ) : ( // Mobile and Tab Version, Hidden on desktop
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }} // Improves performance
              sx={{
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  top: '64px' , // Moves the drawer below app bar
                },
              }}
            >
              <List>
                <ListItem key="docs-drawer-header" disablePadding>
                  <ListItemButton>
                    <Box
                      component={NavLink}
                      to="/docs"
                      onClick={handleDrawerToggle}
                      sx={{
                        borderRadius: 1,
                        // "&.active": {
                        //   color: "primary.main",
                        //   fontWeight: "bold",
                        // },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        textDecoration: 'none',
                        color: 'inherit', // Ensures it looks like normal text
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'primary.main', // Optional hover effect
                        },
                        mr: 1,
                      }}
                    >
                      {/* <HomeIcon color="primary" fontSize="small" /> */}
                      <HomeIcon color="primary" fontSize="small" />
                      <Typography variant="h3" sx={{ flexGrow: 1 }}>
                        Documentation
                      </Typography>
                    </Box>
                    <IconButton
                      edge="start"
                      color="inherit"
                      aria-label="docs-menu"
                      onClick={handleDrawerToggle}
                      fontSize="small"
                      sx={{ py: 0 }}
                    >
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{ transform: 'rotate(180deg)', transition: '0.3s' }}
                      />
                    </IconButton>
                  </ListItemButton>
                </ListItem>
                <Divider sx={{ mt: 1, mb: 1 }} />
                <DocsNavListItems onClick={handleDrawerToggle}/>
              </List>
            </Drawer>
          )}

          {/* Vertical Divider */}
          {/* <Divider orientation="vertical" flexItem /> */}

          {/* Main Content */}
          <Box
            sx={{
              flexGrow: 1,
              p: 3,
              bgcolor: 'background.paper',
              minHeight: '70vh',
            }}
          >
            <Outlet />

            {/* Pagination Buttons */}
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}
            >
              {prevPage ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  component={NavLink}
                  to={prevPage.route}
                  startIcon={<NavigateBeforeOutlinedIcon />}
                >
                  {prevPage.label}
                </Button>
              ) : (
                <Box />
              )}{' '}
              {/* Empty box for alignment */}
              {nextPage ? (
                <Button
                  variant="contained"
                  color="primary"
                  component={NavLink}
                  to={nextPage.route}
                  endIcon={<NavigateNextOutlinedIcon />}
                >
                  {nextPage.label}
                </Button>
              ) : (
                <Box />
              )}{' '}
              {/* Empty box for alignment */}
            </Box>

            <Copyright sx={{ mt: 8 }} />
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default DocsLayout;
